import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { encounterAPI, adminAPI } from '../../lib/api';
import { 
  Stethoscope, 
  ClipboardText, 
  Users,
  Robot,
  CaretRight,
  UserCircle,
  Clock
} from '@phosphor-icons/react';
import WaitingRoomPanel from '../../components/staff/WaitingRoomPanel';
import { HealthAlertsPanel } from '../../components/staff/HealthAlertsPanel';

export default function ProviderDashboard() {
  const { user } = useAuth();
  const [encounters, setEncounters] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [encountersRes, statsRes] = await Promise.all([
        encounterAPI.getAll(),
        adminAPI.getStats(),
      ]);
      setEncounters(encountersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'status-pending';
      case 'in_progress': return 'status-in-progress';
      case 'completed': return 'status-completed';
      default: return 'status-pending';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  const pendingEncounters = encounters.filter(e => e.status !== 'completed');
  const completedToday = encounters.filter(e => 
    e.status === 'completed' && 
    new Date(e.completed_at).toDateString() === new Date().toDateString()
  ).length;

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Provider Dashboard
        </h1>
        <p className="text-slate-500 mt-1">Welcome, Dr. {user?.last_name}. Decision-ready encounters await.</p>
      </div>

      <HealthAlertsPanel detailPath="/provider/health-devices" />

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="kpi-card card-hover" data-testid="provider-pending">
          <div className="flex items-center justify-between">
            <div>
              <div className="kpi-value">{pendingEncounters.length}</div>
              <div className="kpi-label">Pending Encounters</div>
            </div>
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock size={24} className="text-amber-600" />
            </div>
          </div>
        </div>

        <div className="kpi-card card-hover" data-testid="provider-completed-today">
          <div className="flex items-center justify-between">
            <div>
              <div className="kpi-value">{completedToday}</div>
              <div className="kpi-label">Completed Today</div>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Stethoscope size={24} className="text-green-600" />
            </div>
          </div>
        </div>

        <div className="kpi-card card-hover" data-testid="provider-active-encounters">
          <div className="flex items-center justify-between">
            <div>
              <div className="kpi-value">{stats?.active_encounters || 0}</div>
              <div className="kpi-label">Active Encounters</div>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <ClipboardText size={24} className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="kpi-card card-hover" data-testid="provider-total-patients">
          <div className="flex items-center justify-between">
            <div>
              <div className="kpi-value">{stats?.total_patients || 0}</div>
              <div className="kpi-label">Total Patients</div>
            </div>
            <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
              <Users size={24} className="text-teal-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <a 
          href="/provider/encounters" 
          className="card-clinical p-6 card-hover flex items-center justify-between"
          data-testid="quick-action-encounters"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
              <Stethoscope size={24} className="text-teal-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Patient Encounters</h3>
              <p className="text-sm text-slate-500">View and manage encounters</p>
            </div>
          </div>
          <CaretRight size={20} className="text-slate-400" />
        </a>

        <a 
          href="/provider/ai-scribe" 
          className="card-clinical p-6 card-hover flex items-center justify-between ai-feature-card"
          data-testid="quick-action-ai-scribe"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Robot size={24} className="text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">AI Scribe</h3>
              <p className="text-sm text-slate-500">Automated documentation</p>
            </div>
          </div>
          <CaretRight size={20} className="text-slate-400" />
        </a>

        <a 
          href="/provider/messages" 
          className="card-clinical p-6 card-hover flex items-center justify-between"
          data-testid="quick-action-messages"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <ClipboardText size={24} className="text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Messages</h3>
              <p className="text-sm text-slate-500">Patient communications</p>
            </div>
          </div>
          <CaretRight size={20} className="text-slate-400" />
        </a>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Waiting Room Panel */}
        <WaitingRoomPanel />

        {/* Encounters List */}
        <div className="card-clinical">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-slate-900">Ready for Review</h2>
              <a href="/provider/encounters" className="text-teal-600 text-sm font-medium flex items-center gap-1">
                View All <CaretRight size={14} />
              </a>
            </div>
          </div>
        
        {pendingEncounters.length === 0 ? (
          <div className="p-12 text-center">
            <Stethoscope size={48} className="text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No pending encounters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-clinical" data-testid="encounters-table">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingEncounters.slice(0, 10).map((encounter) => (
                  <tr key={encounter.id} className="queue-row" data-testid={`encounter-row-${encounter.id}`}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                          <UserCircle size={18} className="text-slate-400" />
                        </div>
                        <div className="font-medium text-slate-900">{encounter.patient_name}</div>
                      </div>
                    </td>
                    <td className="capitalize">{encounter.encounter_type.replace(/_/g, ' ')}</td>
                    <td>
                      <span className={`status-badge ${getStatusColor(encounter.status)}`}>
                        {encounter.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="text-slate-500">
                      {new Date(encounter.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <a 
                        href={`/provider/encounters/${encounter.id}`}
                        className="text-teal-600 hover:text-teal-700 font-medium text-sm"
                        data-testid={`encounter-btn-${encounter.id}`}
                      >
                        {encounter.status === 'scheduled' ? 'Start' : 'Continue'}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
